<template>
  <portal to="alert-center">
    <div
      v-if="isVisible || pinned || detached"
      id="McNotificationCenter"
      ref="alertCenterRef"
      :class="{
        'notification-sidebar': pinned,
        'notification-draggable-popup': detached,
        'notification-popup': !pinned && !detached,
      }"
      :style="
        detached
          ? { top: popupState.popupPosition.y + 'px', left: popupState.popupPosition.x + 'px' }
          : {}
      "
    >
      <div
        class="notification-header"
        :style="detached ? { cursor: 'move' } : {}"
        @mousedown="startDrag"
        @mouseup="stopDrag"
      >
        <h5 class="m-0">
          {{ $t('dashboard.notification') }}
          <i
            v-if="hasAccess(['e4b-alert-center-rule-setting:read'])"
            class="mc-fa icon-outline_settings fs-5"
            @click="toggleSettingsDropdown"
          ></i>
          <div class="notification-header__setting" :class="{ show: showSettingsDropdown }">
            <div class="notification-header__setting-item" @click="openSetting">
              {{ $t('notification.setting.title') }}
            </div>
            <div v-if="hasUrgentAlertScope" class="notification-header__setting-item">
              <span>{{ $t('notification.action.repeatNotification') }}</span>
              <i
                v-b-tooltip.hover
                class="mc-fa icon-outline_info"
                :title="$t('notification.reminder.tooltip')"
              ></i>
              <u>{{ `${selectedReminder} Min >` }}</u>
              <div class="notification-header__setting-dd">
                <div class="notification-header__setting-dd-item" @click="unsetReminderAndClose()">
                  {{ $t('notification.reminder.withoutReminder') }}
                </div>
                <div class="notification-header__setting-dd-item" @click="setReminderAndClose(5)">
                  5 {{ $t('global.time.minutes') }}
                </div>
                <div class="notification-header__setting-dd-item" @click="setReminderAndClose(10)">
                  10 {{ $t('global.time.minutes') }}
                </div>
                <div class="notification-header__setting-dd-item" @click="setReminderAndClose(15)">
                  15 {{ $t('global.time.minutes') }}
                </div>
                <div class="notification-header__setting-dd-item" @click="setReminderAndClose(30)">
                  30 {{ $t('global.time.minutes') }}
                </div>
                <div class="notification-header__setting-dd-item" @click="setReminderAndClose(60)">
                  60 {{ $t('global.time.minutes') }}
                </div>
                <div class="notification-header__setting-dd-item">
                  <span class="blue-500">
                    <a
                      href="https://kb.mceasy.com/docs/pengaturan-interval-notifikasi-mendesak/"
                      target="_blank"
                      class="blue-500"
                      >{{ $t('global.text.moreInfo') }}
                    </a>
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div class="d-flex notification-header__switch">
            <div class="form-check-right form-switch">
              <input v-model="showUnresolvedOnly" class="form-check-input ms-2" type="checkbox" />
              <label>
                {{ $t('controlTower.detail.notChecked') }}
                <span>({{ unresolvedTotalDisplay }})</span>
              </label>
            </div>
          </div>
        </h5>
        <div class="notification-header__layout">
          <i
            v-if="!pinned && !detached"
            class="mc-fa icon-outline_layout fs-5"
            style="cursor: pointer"
            @click="pinPopup"
          ></i>
          <i
            v-if="pinned"
            v-b-tooltip.hover
            title="Spotlight"
            class="mc-fa icon-outline_external-link fs-5"
            style="cursor: pointer"
            @click="detachPopup"
          ></i>
          <i
            v-if="pinned || detached"
            class="mc-fa icon-outline_close fs-5 ms-2"
            style="cursor: pointer"
            @click="closePopup"
          ></i>
        </div>
      </div>

      <div class="accordion mc-accordion mc-accordion--notification" role="tablist">
        <div v-if="!hasUrgentAlertScope" class="mc-urgent-banner">
          <McAlert variant="info">
            <template #prefix>
              <img
                src="@/assets/images/notification/urgent-bell.png"
                alt="Urgent Icon"
                width="40px"
                height="40px"
              />
            </template>
            <template #label>
              <div>
                <p class="text-muted mb-2">
                  {{ $t('notification.alert.urgentNotificationAvailable') }}
                </p>
                <div>
                  <McButton
                    variant="outline-primary"
                    size="small"
                    icon="icon-outline_play-circle"
                    :text="$t('notification.alert.learnMoreVideo')"
                    v-b-modal.urgent-video-modal
                  />
                  <McButton
                    variant="primary"
                    size="small"
                    icon="icon-fill_checkmark-circle-2"
                    :text="$t('notification.alert.activateNow')"
                    class="ms-2"
                    v-b-modal.urgent-active
                  />
                </div>
              </div>
            </template>
          </McAlert>
        </div>
        <div v-else class="mc-accordion-item mc-accordion-item--urgent">
          <div
            header-tag="header"
            role="tab"
            :class="['mc-accordion__header', 'd-flex', 'justify-content-between']"
            v-b-toggle.accordion-1
          >
            <span
              v-b-tooltip.hover
              class="font-size-14"
              :title="$t('notification.text.numberChecked')"
            >
              {{ $t('setting.list.notification.form.urgent') }} ({{ unresolvedUrgentDisplay }})
            </span>
            <span>
              <u v-if="showWsIndicator.urgent">{{ 'i' }}</u>
              <i
                :class="`mc-fa icon-outline_${alertsExpand.urgent ? 'chevron-up' : 'chevron-down'}`"
              ></i>
            </span>
          </div>
          <b-collapse v-model="alertsExpand.urgent" id="accordion-1" visible role="tabpanel">
            <div
              class="m-2"
              v-if="
                alertRuleIds?.urgentAlertRules?.length === 0 && filteredUrgentAlertList?.length == 0
              "
            >
              <div class="mc-accordion--notification-nodata mc-accordion--notification-nodata-add">
                <img :src="illustrationPath" alt="Notification" />
                <div>
                  <strong>{{ $t('setting.list.notification.notificationNotAdded') }}</strong>
                  <p class="mt-2" v-if="getAccessRight()">
                    {{
                      $t('setting.list.notification.addNotification', {
                        type: `"${$t('setting.list.notification.form.urgent')}"`,
                      })
                    }}
                  </p>
                  <p v-else class="mt-2">
                    {{ $t('notification.text.contactAdmin') }}
                  </p>
                </div>
                <McButton
                  v-if="getAccessRight()"
                  variant="outline-primary"
                  size="small"
                  @click="
                    $router.push({
                      name: 'Add Setting Notification',
                    })
                  "
                  icon="icon-outline_plus"
                  :text="$t('global.create')"
                />
              </div>
            </div>
            <div
              v-if="
                alertRuleIds?.urgentAlertRules?.length > 0 &&
                filteredUrgentAlertList?.length == 0 &&
                !showUnresolvedOnly
              "
              class="m-2"
            >
              <div class="mc-accordion--notification-nodata">
                <img :src="illustrationPathNoData" alt="no-notification" />
                <p>{{ $t('notification.text.nothingNotificationUrgent3days') }}</p>
              </div>
            </div>
            <div
              v-if="
                alertRuleIds?.urgentAlertRules?.length > 0 &&
                filteredUrgentAlertList?.length == 0 &&
                showUnresolvedOnly
              "
              class="m-2"
            >
              <div class="mc-accordion--notification-nodata">
                <img :src="illustrationPathNoData" alt="no-notification" />
                <p>{{ $t('notification.text.nothingNotificationUrgentResolved') }}</p>
              </div>
            </div>

            <div v-if="filteredUrgentAlertList.length > 0">
              <div v-if="filteredUrgentAlertList.length > 0" class="notification-list-wrapper">
                <ul class="notification-list">
                  <li
                    v-for="alert in filteredUrgentAlertList"
                    :key="alert.rawAlertId"
                    class="notification-list-item"
                    :class="{
                      unread: !alert.isRead,
                      active: $store.state.notification.activePopups.some(
                        (popup) => popup.id === alert.id
                      ),
                    }"
                  >
                    <a
                      :key="alert.rawAlertId"
                      href="javascript: void(0);"
                      class="text-reset notification-item"
                      :class="{
                        'is-resolved': alert.isResolved,
                        'is-pending': pendingReadAlertIds.includes(alert.rawAlertId),
                      }"
                      :style="
                        pendingReadAlertIds.includes(alert.rawAlertId)
                          ? 'pointer-events: none; opacity: 0.6;'
                          : ''
                      "
                      @click="openPopup(alert)"
                    >
                      <div class="d-flex">
                        <span class="notification-list-item-icon">
                          <img :src="alert.alert.iconUrl ?? ''" :alt="alert.alert.descriptionIdn" />
                        </span>
                        <div class="flex-grow-1 ms-2">
                          <h6 class="mt-0 mb-1">
                            <McBadge
                              v-if="isFO(notification)"
                              class="me-1"
                              :text="
                                alert.alertData?.includes('|') ? alert.alertData?.split('|')[0] : ''
                              "
                              type="button"
                              variant="outlineSuccess"
                            />
                            <span v-if="hasRuleNameScope">
                              {{
                                alert.alertRule
                                  ? alert.alertRule.label
                                  : $i18n.locale === 'id'
                                  ? alert.alert.descriptionIdn
                                  : alert.alert.descriptionEng
                              }}
                            </span>
                            <span v-else>
                              {{
                                $i18n.locale === 'id'
                                  ? alert.alert.descriptionIdn
                                  : alert.alert.descriptionEng
                              }}
                            </span>
                          </h6>
                          <div class="font-size-12">
                            <p class="mb-1">
                              {{
                                `${alert.vehicle?.licensePlate} | ${
                                  hasRuleNameScope
                                    ? $i18n.locale === 'id'
                                      ? alert.alert.descriptionIdn
                                      : alert.alert.descriptionEng
                                    : alert.driver?.fullname ?? '-'
                                }`
                              }}
                            </p>
                            <span class="mb-0 p-0 d-flex justify-content-between">
                              <span class="text-muted">
                                <i class="mdi mdi-clock-outline"></i>
                                {{ alert.timePassed }}
                              </span>
                              <span class="text-muted">
                                {{ momentTime(alert.createdOn) }} {{ getLabelTimezone() }}
                              </span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </b-collapse>
        </div>
        <div
          :class="`mc-accordion-item ${
            hasUrgentAlertScope ? 'mc-accordion-item--important' : 'mc-accordion-item--urgent'
          }`"
        >
          <div
            header-tag="header"
            role="tab"
            :class="[
              'mc-accordion__header',
              'd-flex',
              'justify-content-between',
              !hasUrgentAlertScope ? 'disabled' : '',
            ]"
            v-b-toggle.accordion-2
          >
            <span
              v-b-tooltip.hover
              class="font-size-14"
              :title="$t('notification.text.numberChecked')"
            >
              {{ $t('setting.list.notification.form.important') }} ({{
                unresolvedImportantDisplay
              }})
            </span>
            <span>
              <u v-if="showWsIndicator.important">{{ 'i' }}</u>
              <i
                v-if="hasUrgentAlertScope"
                :class="`mc-fa icon-outline_${
                  alertsExpand.important ? 'chevron-up' : 'chevron-down'
                }`"
              ></i>
            </span>
          </div>
          <b-collapse v-model="alertsExpand.important" id="accordion-2" visible role="tabpanel">
            <div
              v-if="
                alertRuleIds?.importantAlertRules?.length === 0 &&
                filteredImportantAlertList?.length == 0
              "
              class="m-2"
            >
              <div class="mc-accordion--notification-nodata mc-accordion--notification-nodata-add">
                <img :src="illustrationPath" alt="Notification" />
                <div>
                  <strong>{{ $t('setting.list.notification.notificationNotAdded') }}</strong>
                  <p class="mt-2" v-if="getAccessRight()">
                    {{
                      $t('setting.list.notification.addNotification', {
                        type: `"${$t('setting.list.notification.form.important')}"`,
                      })
                    }}
                  </p>
                  <p v-else class="mt-2">
                    {{ $t('notification.text.contactAdmin') }}
                  </p>
                </div>
                <McButton
                  variant="outline-primary"
                  size="small"
                  @click="
                    $router.push({
                      name: 'Add Setting Notification',
                    })
                  "
                  icon="icon-outline_plus"
                  :text="$t('global.create')"
                  v-if="getAccessRight()"
                />
              </div>
            </div>
            <div
              v-if="
                alertRuleIds?.importantAlertRules?.length > 0 &&
                filteredImportantAlertList?.length == 0 &&
                !showUnresolvedOnly
              "
              class="m-2"
            >
              <div class="mc-accordion--notification-nodata">
                <img :src="illustrationPathNoData" alt="no-notification" />
                <p>{{ $t('notification.text.nothingNotificationImportant3days') }}</p>
              </div>
            </div>
            <div
              v-if="
                alertRuleIds?.importantAlertRules?.length > 0 &&
                filteredImportantAlertList?.length == 0 &&
                showUnresolvedOnly
              "
              class="m-2"
            >
              <div class="mc-accordion--notification-nodata">
                <img :src="illustrationPathNoData" alt="no-notification" />
                <p>{{ $t('notification.text.nothingNotificationImportantResolved') }}</p>
              </div>
            </div>
            <div v-if="filteredImportantAlertList.length > 0">
              <div class="notification-list-wrapper">
                <ul class="notification-list">
                  <li
                    v-for="alert in filteredImportantAlertList"
                    :key="alert.rawAlertId"
                    class="notification-list-item"
                    :class="{
                      unread: !alert.isRead,
                      active: $store.state.notification.activePopups.some(
                        (popup) => popup.id === alert.id
                      ),
                    }"
                  >
                    <a
                      :key="alert.rawAlertId"
                      href="javascript: void(0);"
                      class="text-reset notification-item"
                      :class="{
                        'is-resolved': alert.isResolved,
                        'is-pending': pendingReadAlertIds.includes(alert.rawAlertId),
                      }"
                      :style="
                        pendingReadAlertIds.includes(alert.rawAlertId)
                          ? 'pointer-events: none; opacity: 0.6;'
                          : ''
                      "
                      @click="openPopup(alert)"
                    >
                      <div class="d-flex">
                        <span class="notification-list-item-icon">
                          <img :src="alert.alert.iconUrl ?? ''" :alt="alert.alert.descriptionIdn" />
                        </span>
                        <div class="flex-grow-1 ms-2">
                          <h6 class="mt-0 mb-1">
                            <McBadge
                              v-if="isFO(notification)"
                              class="me-1"
                              :text="
                                alert.alertData?.includes('|') ? alert.alertData?.split('|')[0] : ''
                              "
                              type="button"
                              variant="outlineSuccess"
                            />
                            <span v-if="hasRuleNameScope">
                              {{
                                alert.alertRule
                                  ? alert.alertRule.label
                                  : $i18n.locale === 'id'
                                  ? alert.alert.descriptionIdn
                                  : alert.alert.descriptionEng
                              }}
                            </span>
                            <span v-else>
                              {{
                                $i18n.locale === 'id'
                                  ? alert.alert.descriptionIdn
                                  : alert.alert.descriptionEng
                              }}
                            </span>
                          </h6>
                          <div class="font-size-12">
                            <p class="mb-1">
                              {{
                                `${alert.vehicle?.licensePlate} |  ${
                                  hasRuleNameScope
                                    ? $i18n.locale === 'id'
                                      ? alert.alert.descriptionIdn
                                      : alert.alert.descriptionEng
                                    : alert.driver?.fullname ?? '-'
                                }`
                              }}
                            </p>
                            <span class="mb-0 p-0 d-flex justify-content-between">
                              <span class="text-muted">
                                <i class="mdi mdi-clock-outline"></i>
                                {{ alert.timePassed }}
                              </span>
                              <span class="text-muted">
                                {{ momentTime(alert.createdOn) }} {{ getLabelTimezone() }}
                              </span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </b-collapse>
        </div>
      </div>
      <div class="p-2 border-top d-grid">
        <McButton
          variant="secondary"
          :text="$t('notification.text.seeOthersNotification')"
          size="sm"
          block
          @click="toNotificationPage"
        />
      </div>
    </div>
    <ModalVideo @hideUrgentInfo="!hasUrgentAlertScope" />
    <ModalActive @hideUrgentInfo="!hasUrgentAlertScope" />
    <ModalSuccess />
  </portal>
</template>

<script>
import McButton from '@/components/McButton'
import { Helper } from '@/mixin/helper'
import { mapGetters, mapActions } from 'vuex'
import {
  GET_RAW_ALERTS_WITH_ADDRESS,
  UPSERT_RAW_ALERTS_ACTION,
  TOTAL_IMPORTANT_ALERTS,
} from '@/services/api/driverscore/score'
import { MixPanel } from '@/mixin/mixpanel'
import McBadge from '@/components/McBadge'
import ModalVideo from '@/router/layouts/components/Vertical/AlertCenter/Modal/video.vue'
import ModalActive from '@/router/layouts/components/Vertical/AlertCenter/Modal/active.vue'
import ModalSuccess from '@/router/layouts/components/Vertical/AlertCenter/Modal/success.vue'
import McAlert from '@/components/McAlert.vue'

const urgentSoundNotification = require('@/assets/sounds/urgent_notification.mp3')

export default {
  components: {
    McButton,
    McBadge,
    McAlert,
    ModalVideo,
    ModalActive,
    ModalSuccess,
  },
  mixins: [Helper, MixPanel],
  props: {
    notification: {
      type: Object,
      required: true,
    },
    isVisible: {
      type: Boolean,
      required: true,
    },
    alertRulesObj: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      viewportHeight: window.innerHeight,
      isDragging: false,
      offset: { x: 0, y: 0 },
      alertList: [],
      urgentAlertNotificationStatuses: [],
      importantAlertNotificationStatuses: [],
      urgentAlertList: [],
      importantAlertList: [],
      wsAlert: [],
      popupState: this.$store.state.notification.popupState,
      timeUpdateInterval: null,
      tempReadAlerts: [],
      showSettingsDropdown: false,
      selectedReminder: 5,
      showUnresolvedOnly: false,
      reminderIntervalId: null,
      showWsIndicator: {
        urgent: false,
        important: false,
      },
      unresolvedUrgentAlertList: [],
      unresolvedImportantAlertList: [],
      loadedUnresolvedAccordions: {
        urgent: false,
        important: false,
      },
      startDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      endDate: new Date().toISOString(),
      openAccordion: 'important',
      showUrgentAlertExistInfo: true,
      pendingReadAlertIds: [],
      alertsExpand: {
        urgent: true,
        important: true,
      },
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      isWhitelabel: 'identity/isWhitelabel',
      isMetrack: 'identity/isMetrack',
      isAlertListLoaded: 'notification/isAlertListLoaded',
      unresolvedUrgentCountFromStore: 'notification/unresolvedUrgentAlertCount',
      unresolvedImportantCountFromStore: 'notification/unresolvedImportantAlertCount',
    }),
    calculatedHeight() {
      return this.pinned ? 750 : Math.min(this.viewportHeight * 0.8, 720) // 80% of viewport height or 720px max
    },
    pinned() {
      return this.hasAccess(['e4a-alert-center-main:read'])
        ? this.$store.state.sidebar.notifSideBarOpen
        : false
    },
    detached() {
      return this.hasAccess(['e4a-alert-center-main:read'])
        ? this.$store.state.notification.popupState.alertCenterPopup
        : false
    },
    illustrationPath() {
      if (this.isWhitelabel() || this.isMetrack()) {
        return require(`@/assets/images/illustration/whitelabel/no-notification.png`)
      }
      return require(`@/assets/images/illustration/saas/no-notification.png`)
    },
    illustrationPathNoData() {
      return require(`@/assets/images/success-icon.png`)
    },
    illustrationPathUrgent() {
      return require(`@/assets/images/bell-icon.svg`)
    },
    address() {
      return this.$store.state.map.address
    },
    filteredUrgentAlertList() {
      if (!this.showUnresolvedOnly) return this.urgentAlertList
      return this.getMergedUnresolvedAlerts(this.urgentAlertList, this.unresolvedUrgentAlertList)
    },
    filteredImportantAlertList() {
      if (!this.showUnresolvedOnly) return this.importantAlertList
      return this.getMergedUnresolvedAlerts(
        this.importantAlertList,
        this.unresolvedImportantAlertList
      )
    },
    unresolvedUrgentCount() {
      return this.urgentAlertList.filter(
        (alert) => alert.isResolved !== true && alert.isFalseAlert !== true
      ).length
    },
    unresolvedImportantCount() {
      return this.importantAlertList.filter(
        (alert) => alert.isResolved !== true && alert.isFalseAlert !== true
      ).length
    },
    filteredTotalCount() {
      return this.unresolvedUrgentCount + this.unresolvedImportantCount
    },
    unresolvedUrgentDisplay() {
      const total = this.hasUrgentAlertScope ? this.unresolvedUrgentCountFromStore : 0
      return total > 99 ? '99+' : total
    },
    unresolvedImportantDisplay() {
      return this.unresolvedImportantCountFromStore > 99
        ? '99+'
        : this.unresolvedImportantCountFromStore
    },
    unresolvedTotalDisplay() {
      const total =
        this.unresolvedImportantCountFromStore +
        (this.hasUrgentAlertScope ? this.unresolvedUrgentCountFromStore : 0)
      return total > 99 ? '99+' : total
    },
    isFO() {
      const FOalertCode = [143, 142, 141, 140, 139, 138, 137]
      return (item) => {
        return FOalertCode.includes(item.alertCode)
      }
    },

    hasUrgentAlertScope() {
      return this.hasAccess('e4e-alert-center-urgent:read')
    },
    defaultAccordion() {
      return this.hasUrgentAlertScope ? 'urgent' : 'important'
    },
    hasRuleNameScope() {
      return this.hasAccess('e4J-alert-rule-name-as-title:read')
    },
    alertRuleIds() {
      return this.$store.state.notification.alertRuleIds
    },
  },
  watch: {
    showUnresolvedOnly: {
      handler(newValue) {
        localStorage.setItem('alertCenterShowUnresolvedOnly', JSON.stringify(newValue))
        if (newValue) {
          this.loadedUnresolvedAccordions = {
            urgent: false,
            important: false,
          }
          if (this.openAccordion === 'urgent') {
            this.loadUnresolvedUrgentData()
          } else if (this.openAccordion === 'important') {
            this.loadUnresolvedImportantData()
          }
        } else {
          this.unresolvedUrgentAlertList = []
          this.unresolvedImportantAlertList = []
          this.loadedUnresolvedAccordions = {
            urgent: false,
            important: false,
          }
        }
      },
      immediate: false,
    },
    openAccordion: {
      handler(newValue) {
        if (this.showUnresolvedOnly && newValue) {
          if (
            newValue === 'urgent' &&
            !this.loadedUnresolvedAccordions.urgent &&
            this.hasUrgentAlertScope
          ) {
            this.loadUnresolvedUrgentData()
          } else if (newValue === 'important' && !this.loadedUnresolvedAccordions.important) {
            this.loadUnresolvedImportantData()
          }
        }
      },
      immediate: false,
    },
    notification: {
      deep: true,
      immediate: true,
      handler(val) {
        // Guard: Check if alert rules are properly loaded as arrays
        if (
          !val?.alertRuleIds ||
          !this.alertRuleIds ||
          !Array.isArray(this.alertRuleIds.urgentAlertRules) ||
          !Array.isArray(this.alertRuleIds.importantAlertRules) ||
          !Array.isArray(this.alertRuleIds.autoPopupAlertRules)
        ) {
          return
        }

        const notificationId = val.rawAlertId
        const hasExistingAlert = this.alertList.some(
          (alert) => String(alert.rawAlertId) === String(notificationId)
        )

        if (hasExistingAlert) {
          return
        }

        const isUrgent = val.alertRuleIds.some((ruleId) =>
          this.alertRuleIds.urgentAlertRules.includes(ruleId)
        )

        const isImportant = val.alertRuleIds.some((ruleId) =>
          this.alertRuleIds.importantAlertRules.includes(ruleId)
        )

        const isAutoPopup = val.alertRuleIds.some((ruleId) =>
          this.alertRuleIds.autoPopupAlertRules.includes(ruleId)
        )

        // Ensure urgent takes precedence - if urgent, it's not important
        const notification = {
          ...val,
          id: notificationId,
          isRead: false,
          isResolved: false,
          isUrgent: isUrgent,
          isImportant: isUrgent ? false : isImportant,
          timePassed: this.checkTimePassed(val.createdOn),
        }

        if (isUrgent && this.hasUrgentAlertScope) {
          this.$set(this, 'urgentAlertList', [notification, ...this.urgentAlertList.slice(0, 19)])
          this.incrementUnresolvedAlertCount('urgent')
          this.triggerWsIndicator('urgent')
          if (this.showUnresolvedOnly && this.openAccordion === 'urgent') {
            if (this.loadedUnresolvedAccordions.urgent) {
              this.unresolvedUrgentAlertList = [
                notification,
                ...this.unresolvedUrgentAlertList.slice(0, 19),
              ]
            }
          }
        } else if (isImportant) {
          this.$set(this, 'importantAlertList', [
            notification,
            ...this.importantAlertList.slice(0, 19),
          ])
          this.incrementUnresolvedAlertCount('important')
          this.triggerWsIndicator('important')
          if (this.showUnresolvedOnly && this.openAccordion === 'important') {
            if (this.loadedUnresolvedAccordions.important) {
              this.unresolvedImportantAlertList = [
                notification,
                ...this.unresolvedImportantAlertList.slice(0, 19),
              ]
            }
          }
        }

        this.alertList = [...this.urgentAlertList, ...this.importantAlertList].sort(
          (a, b) => new Date(b.createdOn) - new Date(a.createdOn)
        )
        this.$store.commit('notification/SET_ALERT_LIST', this.alertList)

        if (isAutoPopup) {
          this.openPopup(notification)
        }
      },
    },
    popupState: {
      handler(newState) {
        localStorage.setItem('alertCenterPopup', JSON.stringify(newState))
      },
      deep: true,
    },
    '$store.state.notification.alertList': {
      handler(newAlertList) {
        if (newAlertList) {
          this.alertList = [...newAlertList]

          this.urgentAlertList = newAlertList.filter((alert) => alert.isUrgent)

          this.importantAlertList = newAlertList.filter(
            (alert) => alert.isImportant && !alert.isUrgent
          )
        }
      },
      deep: true,
      immediate: true,
    },
  },
  async mounted() {
    window.addEventListener('resize', this.updateViewportHeight)
    document.addEventListener('mousedown', this.handleClickOutside)

    if (this.hasAccess(['e4a-alert-center-main:read'])) {
      const sidebar = localStorage.getItem('notifSideBarOpen')
      if (sidebar !== null) {
        this.$store.commit('sidebar/toggleNotifSideBar', JSON.parse(sidebar))
      }
      const popup = localStorage.getItem('alertCenterPopup')
      if (popup) {
        const parsedState = JSON.parse(popup)
        this.$store.commit('notification/setPopupState', parsedState)
        this.popupState = parsedState
      }
      if (this.$store.state.sidebar.notifSideBarOpen) {
        document.body.classList.add('notification-center')
      } else {
        document.body.classList.remove('notification-center')
      }

      const savedShowUnresolvedOnly = localStorage.getItem('alertCenterShowUnresolvedOnly')
      if (savedShowUnresolvedOnly !== null) {
        try {
          this.showUnresolvedOnly = JSON.parse(savedShowUnresolvedOnly)
        } catch {
          localStorage.removeItem('alertCenterShowUnresolvedOnly')
          this.showUnresolvedOnly = false
        }
      }

      // Wait for alertRuleIds to be available before loading data
      // This ensures alerts can be properly categorized as urgent/important
      await this.ensureAlertRulesReady()

      await this.getData()

      this.startTimeUpdates()
    }

    const savedReminder = localStorage.getItem('reminderSetting')
    if (savedReminder) {
      this.selectedReminder = Number(savedReminder)
    } else {
      this.selectedReminder = 0
    }

    document.addEventListener('click', this.handleClickOutsideSettings)
    if (this.selectedReminder > 0) {
      const existingInterval = localStorage.getItem('alertCenterRepeatInterval')
      if (existingInterval && !this.reminderIntervalId) {
        try {
          const intervalData = JSON.parse(existingInterval)
          const now = Date.now()
          const timeElapsed = now - intervalData.startTime
          const totalIntervalTime = intervalData.intervalMinutes * 60 * 1000
          const remainingTime = Math.max(0, totalIntervalTime - timeElapsed)

          if (remainingTime > 1000) {
            await this.checkUrgentReminder(false)

            this.reminderIntervalId = setTimeout(() => {
              this.checkUrgentReminder()
              this.reminderIntervalId = setInterval(() => {
                this.checkUrgentReminder()
              }, intervalData.intervalMinutes * 60 * 1000)
              const newIntervalData = {
                startTime: Date.now(),
                intervalMinutes: intervalData.intervalMinutes,
              }
              localStorage.setItem('alertCenterRepeatInterval', JSON.stringify(newIntervalData))
            }, remainingTime)
          } else {
            this.setReminder(this.selectedReminder || 5)
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error restoring interval:', error)
          this.setReminder(this.selectedReminder || 5)
        }
      } else if (!this.reminderIntervalId) {
        this.setReminder(this.selectedReminder || 5)
      }
    }
    this.openAccordion = this.defaultAccordion

    if (this.showUnresolvedOnly && this.openAccordion) {
      if (this.openAccordion === 'urgent' && !this.loadedUnresolvedAccordions.urgent) {
        this.loadUnresolvedUrgentData()
      } else if (this.openAccordion === 'important' && !this.loadedUnresolvedAccordions.important) {
        this.loadUnresolvedImportantData()
      }
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateViewportHeight)
    document.removeEventListener('mousedown', this.handleClickOutside)
    clearInterval(this.timeUpdateInterval)
    if (this.reminderIntervalId) {
      clearInterval(this.reminderIntervalId)
      this.reminderIntervalId = null
    }
    document.removeEventListener('click', this.handleClickOutsideSettings)
  },
  methods: {
    ...mapActions({
      setAlertListLoaded: 'notification/setAlertListLoaded',
      setUnresolvedAlertCounts: 'notification/setUnresolvedAlertCounts',
      incrementUnresolvedAlertCount: 'notification/incrementUnresolvedAlertCount',
    }),
    openTheAccordion(type) {
      const isOpened = this.openAccordion === type
      this.openAccordion = isOpened ? null : type
      this.showWsIndicator[type] = false
    },
    isUnresolvedAlert(alert) {
      return alert?.isResolved !== true && alert?.isFalseAlert !== true
    },
    getMergedUnresolvedAlerts(alertList = [], unresolvedAlertList = []) {
      const latestAlerts = new Map(
        alertList.map((alert) => [String(alert.rawAlertId ?? alert.id), alert])
      )
      const mergedAlerts = []

      unresolvedAlertList.forEach((alert) => {
        const alertId = String(alert.rawAlertId ?? alert.id)
        const latestAlert = latestAlerts.get(alertId)
        const candidate = latestAlert || alert

        if (this.isUnresolvedAlert(candidate)) {
          mergedAlerts.push(candidate)
        }

        latestAlerts.delete(alertId)
      })

      latestAlerts.forEach((alert) => {
        if (this.isUnresolvedAlert(alert)) {
          mergedAlerts.push(alert)
        }
      })

      return mergedAlerts.sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn)).slice(0, 20)
    },

    async ensureAlertRulesReady() {
      // Wait for alert rules to be loaded from Vuex store
      // This is necessary because alerts need to be categorized as urgent/important
      const maxWaitTime = 5000 // 5 seconds max
      const startTime = Date.now()

      while (!this.alertRuleIds && Date.now() - startTime < maxWaitTime) {
        await new Promise((resolve) => setTimeout(resolve, 100))
      }
    },

    async getData() {
      if (this.isAlertListLoaded) return

      if (this.hasUrgentAlertScope) {
        const urgentRes = await this.$apollo.query({
          query: GET_RAW_ALERTS_WITH_ADDRESS,
          variables: {
            filter: {
              pagination: {
                page: 1,
                show: 20,
              },
              orderBy: [JSON.stringify({ createdOn: 'desc' })],
              isUrgent: true,
              startDate: this.startDate,
              endDate: this.endDate,
            },
          },
          fetchPolicy: 'no-cache',
        })

        if (urgentRes.data.rawAlerts.isSuccessful) {
          const urgentData = urgentRes.data.rawAlerts.rawAlerts?.data
          this.urgentAlertNotificationStatuses = this.generateIds(urgentData)
        }
      }

      const importantRes = await this.$apollo.query({
        query: GET_RAW_ALERTS_WITH_ADDRESS,
        variables: {
          filter: {
            pagination: {
              page: 1,
              show: 20,
            },
            orderBy: [JSON.stringify({ createdOn: 'desc' })],
            isImportant: true,
            startDate: this.startDate,
            endDate: this.endDate,
          },
        },
        fetchPolicy: 'no-cache',
      })

      if (importantRes.data.rawAlerts.isSuccessful) {
        const importantData = importantRes.data.rawAlerts.rawAlerts?.data
        this.importantAlertNotificationStatuses = this.generateIds(importantData)
      }

      await this.mergeAlertList()
      await this.checkTotalImportantAlerts()

      this.setAlertListLoaded(true)
    },

    async mergeAlertList() {
      try {
        const [
          resolvedWsAlert,
          resolvedUrgentAlertNotificationStatuses,
          resolvedImportantAlertNotificationStatuses,
        ] = await Promise.all([
          this.wsAlert instanceof Promise ? this.wsAlert : Promise.resolve(this.wsAlert),
          this.urgentAlertNotificationStatuses instanceof Promise
            ? this.urgentAlertNotificationStatuses
            : Promise.resolve(this.urgentAlertNotificationStatuses),
          this.importantAlertNotificationStatuses instanceof Promise
            ? this.importantAlertNotificationStatuses
            : Promise.resolve(this.importantAlertNotificationStatuses),
        ])

        const urgentAlertsMap = new Map()
        resolvedWsAlert.forEach((alert) => {
          if (alert.isUrgent) {
            urgentAlertsMap.set(alert.id, alert)
          }
        })

        resolvedUrgentAlertNotificationStatuses.forEach((alert) => {
          urgentAlertsMap.set(alert.id, alert)
        })

        const filteredUrgentAlerts = Array.from(urgentAlertsMap.values())

        this.urgentAlertList = [
          ...filteredUrgentAlerts.sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn)),
        ]

        this.urgentAlertList = this.urgentAlertList.map((alert) => {
          const existingAlert = this.tempReadAlerts.find((item) => item.id === alert.id)
          return existingAlert ? existingAlert : alert
        })

        const importantAlertsMap = new Map()
        resolvedWsAlert.forEach((alert) => {
          if (alert.isImportant && !alert.isUrgent) {
            importantAlertsMap.set(alert.id, alert)
          }
        })

        resolvedImportantAlertNotificationStatuses.forEach((alert) => {
          if (!alert.isUrgent) {
            importantAlertsMap.set(alert.id, alert)
          }
        })

        const filteredImportantAlerts = Array.from(importantAlertsMap.values())

        this.importantAlertList = filteredImportantAlerts.sort(
          (a, b) => new Date(b.createdOn) - new Date(a.createdOn)
        )

        this.importantAlertList = this.importantAlertList.map((alert) => {
          const existingAlert = this.tempReadAlerts.find((item) => item.id === alert.id)
          return existingAlert ? existingAlert : alert
        })

        this.alertList = [...this.urgentAlertList, ...this.importantAlertList].sort(
          (a, b) => new Date(b.createdOn) - new Date(a.createdOn)
        )

        this.$store.commit('notification/SET_ALERT_LIST', this.alertList)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error)
        this.alertList = []
        this.urgentAlertList = []
        this.importantAlertList = []
      }
    },

    toggleSettingsDropdown() {
      this.showSettingsDropdown = !this.showSettingsDropdown
    },

    handleClickOutsideSettings(event) {
      const settingsIcon = event.target.closest('.icon-outline_settings')
      const settingsDropdown = event.target.closest('.notification-header__setting')

      if (!settingsIcon && !settingsDropdown) {
        this.showSettingsDropdown = false
      }
    },

    async checkTotalImportantAlerts() {
      const res = await this.$apollo.query({
        query: TOTAL_IMPORTANT_ALERTS,
        variables: {
          filter: { startDate: this.startDate, endDate: this.endDate },
        },
        fetchPolicy: 'no-cache',
      })
      const alerts = res?.data?.totalImportantAlerts?.totalImportantAlerts || []
      const urgent = alerts.find((a) => a.alertUrgencyType === 'Urgent')
      const important = alerts.find((a) => a.alertUrgencyType === 'Important')
      this.setUnresolvedAlertCounts({
        urgent: this.hasUrgentAlertScope ? urgent?.unresolved : 0,
        important: important?.unresolved || 0,
      })
    },

    async checkUrgentReminder(playSound = true, refreshCounts = true) {
      try {
        if (refreshCounts) {
          await this.checkTotalImportantAlerts()
        }

        if (this.unresolvedUrgentCountFromStore > 0) {
          if (playSound) {
            const audio = new Audio(urgentSoundNotification)
            audio.play()
          }
          this.$root.$emit('urgent-unresolved', true)
        } else if (this.unresolvedUrgentCountFromStore === 0) {
          if (this.reminderIntervalId) {
            clearInterval(this.reminderIntervalId)
            this.reminderIntervalId = null
          }
          localStorage.removeItem('alertCenterRepeatInterval')
          this.$root.$emit('urgent-unresolved', false)
        } else {
          this.$root.$emit('urgent-unresolved', false)
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Reminder check failed', e)
        this.$root.$emit('urgent-unresolved', false)
      }
    },
    setReminder(value) {
      this.selectedReminder = value

      if (value === 0) {
        localStorage.removeItem('reminderSetting')
        localStorage.removeItem('alertCenterRepeatInterval')

        if (this.reminderIntervalId) {
          clearInterval(this.reminderIntervalId)
          this.reminderIntervalId = null
        }

        this.$root.$emit('urgent-unresolved', false)
        return
      }

      localStorage.setItem('reminderSetting', value)

      if (this.reminderIntervalId) {
        clearInterval(this.reminderIntervalId)
        this.reminderIntervalId = null
      }
      const remainingTime = value * 60 * 1000
      const intervalData = {
        startTime: Date.now(),
        intervalMinutes: value,
      }
      localStorage.setItem('alertCenterRepeatInterval', JSON.stringify(intervalData))

      this.reminderIntervalId = setTimeout(() => {
        this.checkUrgentReminder()
        this.reminderIntervalId = setInterval(() => {
          this.checkUrgentReminder()
        }, value * 60 * 1000)

        const newIntervalData = {
          startTime: Date.now(),
          intervalMinutes: value,
        }
        localStorage.setItem('alertCenterRepeatInterval', JSON.stringify(newIntervalData))
      }, remainingTime)

      this.checkUrgentReminder()
    },
    unsetReminderAndClose() {
      this.selectedReminder = 0
      localStorage.removeItem('reminderSetting')

      if (this.reminderIntervalId) {
        clearInterval(this.reminderIntervalId)
        this.reminderIntervalId = null
      }

      localStorage.removeItem('alertCenterRepeatInterval')
      this.$mcToast.success(this.$t('global.alert.text.changed'))
      this.showSettingsDropdown = false
    },

    setReminderAndClose(value) {
      this.setReminder(value)
      this.$mcToast.success(this.$t('global.alert.text.changed'))
      this.showSettingsDropdown = false
    },

    pinPopup() {
      this.$store.commit('sidebar/toggleNotifSideBar', true)
      this.$emit('update:isVisible', false)
      document.body.classList.add('notification-center')
    },
    detachPopup() {
      document.body.classList.remove('notification-center')
      this.$store.commit('notification/setPopupState', { alertCenterPopup: true })
      this.popupState.alertCenterPopup = true
      this.$store.commit('sidebar/toggleNotifSideBar', false)
    },
    closePopup() {
      this.$store.commit('notification/setPopupState', { alertCenterPopup: false })
      this.popupState.alertCenterPopup = false
      this.$store.commit('sidebar/toggleNotifSideBar', false)
      this.$emit('update:isVisible', false)
      localStorage.removeItem('notifSideBarOpen')
      document.body.classList.remove('notification-center')
    },
    startDrag(event) {
      if (!this.detached) return
      this.isDragging = true
      this.offset.x = event.clientX - this.popupState.popupPosition.x
      this.offset.y = event.clientY - this.popupState.popupPosition.y
      document.addEventListener('mousemove', this.onDrag)
    },
    onDrag(event) {
      if (!this.isDragging) return
      const element = this.$refs.alertCenterRef ?? this.$el
      const elementRect = element.getBoundingClientRect()

      const maxX = window.innerWidth - elementRect.width
      const maxY = window.innerHeight - elementRect.height

      const newX = Math.min(Math.max(event.clientX - this.offset.x, 0), maxX)
      const newY = Math.min(Math.max(event.clientY - this.offset.y, 0), maxY)

      const newPosition = {
        x: newX,
        y: newY,
      }

      this.popupState.popupPosition = newPosition
      this.$store.commit('notification/setPopupState', {
        popupPosition: newPosition,
      })
    },
    stopDrag() {
      this.isDragging = false
      document.removeEventListener('mousemove', this.onDrag)
    },
    updateViewportHeight() {
      this.viewportHeight = window.innerHeight
    },
    handleClickOutside(event) {
      if (this.pinned || this.detached) {
        return
      }
      const popup = this.$refs.alertCenterRef
      if (popup && !popup.contains(event.target)) {
        this.closePopup()
      }
    },
    checkPopup(notification) {
      const activePopups = this.$store.state.notification.activePopups
      const alertId = notification.rawAlertId ?? notification.id
      const isOpened = activePopups.find((popup) => String(popup.id) === String(alertId))
      if (isOpened) {
        this.$emit('close-popup', alertId)
      } else {
        this.$emit('open-popup', alertId)
      }
    },
    async openPopup(notification) {
      let urgency_level = ''
      if (
        (notification.alertRuleIds &&
          this.alertRuleIds?.urgentAlertRules?.some?.((id) =>
            notification.alertRuleIds.includes(id)
          )) ||
        notification.isUrgent
      ) {
        urgency_level = 'urgent'
      } else if (
        (notification.alertRuleIds &&
          this.alertRuleIds?.importantAlertRules?.some?.((id) =>
            notification.alertRuleIds.includes(id)
          )) ||
        notification.isImportant
      ) {
        urgency_level = 'important'
      }
      this.track('Click each Notification from Alert Center list', {
        'license-plate': notification?.licensePlate,
        'notification-name':
          this.$i18n.locale === 'id'
            ? notification.alert.descriptionIdn
            : notification.alert.descriptionEng,
        'time-passed': notification.timePassed,
        urgency_level: urgency_level,
      })
      if (
        this.hasAccess(['e4f-auto-popup-urgent-alert:read']) &&
        this.$store.state.notification.activePopups.length >= 9
      ) {
        this.$mcToast.warning(this.$t('notification.text.limitNotification'), { timeout: 1000 })
        return
      }

      this.checkPopup(notification)

      if (!notification.isRead && !this.pendingReadAlertIds.includes(notification.rawAlertId)) {
        this.pendingReadAlertIds.push(notification.rawAlertId)

        // Optimistic update: set mark read before await to prevent user rage clicks
        notification.isRead = true
        this.urgentAlertList.forEach((item) => {
          if (item.rawAlertId === notification.rawAlertId) {
            item.isRead = true
            this.tempReadAlerts.push(item)
          }
        })
        this.importantAlertList.forEach((item) => {
          if (item.rawAlertId === notification.rawAlertId) {
            item.isRead = true
            this.tempReadAlerts.push(item)
          }
        })
        this.alertList.forEach((item) => {
          if (item.rawAlertId === notification.rawAlertId) {
            item.isRead = true
            this.tempReadAlerts.push(item)
          }
        })

        try {
          const input = {
            actionType: 'Read',
            data: null,
            rawAlertId: [notification.rawAlertId],
            selectionMode: 'Selection',
          }
          const update = await this.$apollo.mutate({
            mutation: UPSERT_RAW_ALERTS_ACTION,
            variables: {
              input: input,
            },
          })

          if (!update.data.upsertRawAlertsAction.isSuccessful) {
            notification.isRead = false
            const rollback = (list) => {
              list.forEach((item) => {
                if (item.rawAlertId === notification.rawAlertId) {
                  item.isRead = false
                }
              })
            }
            rollback(this.urgentAlertList)
            rollback(this.importantAlertList)
            rollback(this.alertList)
            this.tempReadAlerts = this.tempReadAlerts.filter(
              (item) => item.rawAlertId !== notification.rawAlertId
            )
          }
        } catch (e) {
          notification.isRead = false
          const rollback = (list) => {
            list.forEach((item) => {
              if (item.rawAlertId === notification.rawAlertId) {
                item.isRead = false
              }
            })
          }
          rollback(this.urgentAlertList)
          rollback(this.importantAlertList)
          rollback(this.alertList)
          this.tempReadAlerts = this.tempReadAlerts.filter(
            (item) => item.rawAlertId !== notification.rawAlertId
          )
        } finally {
          this.pendingReadAlertIds = this.pendingReadAlertIds.filter(
            (id) => id !== notification.rawAlertId
          )
          this.$store.commit('notification/SET_ALERT_LIST', this.alertList)
        }
      }
    },
    openSetting() {
      this.$router.push({
        name: 'Setting Notification List',
      })
    },
    generateIds(data) {
      return data.map((item) => {
        // Ensure urgent takes precedence - if urgent, it's not important
        const isUrgent = item.isUrgent || false
        const isImportant = item.isImportant || false

        return {
          id: item.rawAlertId,
          ...item,
          timePassed: this.checkTimePassed(item.createdOn),
          isUrgent: isUrgent,
          isImportant: isUrgent ? false : isImportant,
        }
      })
    },
    startTimeUpdates() {
      this.timeUpdateInterval = setInterval(() => {
        this.urgentAlertList = this.urgentAlertList.map((item) => ({
          ...item,
          timePassed: this.checkTimePassed(item.createdOn),
        }))
        this.importantAlertList = this.importantAlertList.map((item) => ({
          ...item,
          timePassed: this.checkTimePassed(item.createdOn),
        }))
        this.alertList = this.alertList.map((item) => ({
          ...item,
          timePassed: this.checkTimePassed(item.createdOn),
        }))
        this.$store.commit('notification/SET_ALERT_LIST', this.alertList)
      }, 1000)
    },
    toNotificationPage() {
      if (this.hasAccess(['e1j-log-notification:read'])) {
        this.$router.push({ name: 'Notification Log' })
      } else {
        this.$router.push({ name: 'Notifikasi' })
      }
    },
    triggerWsIndicator(type) {
      if (type !== 'urgent' && type !== 'important') return
      this.showWsIndicator[type] = true
    },
    async loadUnresolvedUrgentData() {
      if (!this.isVisible && !this.pinned && !this.detached) return
      if (this.loadedUnresolvedAccordions.urgent) return

      try {
        const urgentRes = await this.$apollo.query({
          query: GET_RAW_ALERTS_WITH_ADDRESS,
          variables: {
            filter: {
              pagination: {
                page: 1,
                show: 20,
              },
              orderBy: [JSON.stringify({ createdOn: 'desc' })],
              isUrgent: true,
              isResolved: false,
              isFalseAlert: false,
            },
          },
          fetchPolicy: 'no-cache',
        })

        if (urgentRes.data.rawAlerts.isSuccessful) {
          const urgentData = urgentRes.data.rawAlerts.rawAlerts?.data
          this.unresolvedUrgentAlertList = this.generateIds(urgentData)
          this.loadedUnresolvedAccordions.urgent = true
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load unresolved urgent data', error)
      }
    },
    async loadUnresolvedImportantData() {
      if (!this.isVisible && !this.pinned && !this.detached) return
      if (this.loadedUnresolvedAccordions.important) return

      try {
        const importantRes = await this.$apollo.query({
          query: GET_RAW_ALERTS_WITH_ADDRESS,
          variables: {
            filter: {
              pagination: {
                page: 1,
                show: 20,
              },
              orderBy: [JSON.stringify({ createdOn: 'desc' })],
              isImportant: true,
              isResolved: false,
              isFalseAlert: false,
            },
          },
          fetchPolicy: 'no-cache',
        })

        if (importantRes.data.rawAlerts.isSuccessful) {
          const importantData = importantRes.data.rawAlerts.rawAlerts?.data
          this.unresolvedImportantAlertList = this.generateIds(importantData)
          this.loadedUnresolvedAccordions.important = true
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load unresolved important data', error)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import './style.scss';
</style>
